import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppConfig } from '../config/AppConfigContext';
import { LoadingSpinner } from '../components/feedback';
import * as apiDocsUtils from '../helpers/api_docs_utils';
import type { OpenApiSpec } from '../types/index';

interface ElementsApiElement extends HTMLElement {
  apiDescriptionDocument?: OpenApiSpec;
}

const ApiDocsPage: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { allyApi } = useAppConfig();
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [apiSpec, setApiSpec] = useState<OpenApiSpec | null>(null);
  const [specError, setSpecError] = useState<string | null>(null);
  const [isLoadingSpec, setIsLoadingSpec] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then(setToken);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch the API spec with authentication
  useEffect(() => {
    if (!token || !allyApi) return;

    const fetchApiSpec = async () => {
      setIsLoadingSpec(true);
      setSpecError(null);

      try {
        // Use proxy in development, direct API call in production
        const isDevelopment = window.location.hostname === 'localhost';
        const specUrl = apiDocsUtils.buildApiSpecUrl(allyApi, isDevelopment);

        const response = await fetch(specUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch API spec: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!apiDocsUtils.isJsonContentType(contentType)) {
          const responsePreview = await response.text().catch(() => '');
          const sanitizedPreview = responsePreview.replace(/\s+/g, ' ').trim().slice(0, 120);
          throw new Error(
            `Expected JSON API spec but received ${contentType ?? 'unknown content type'}${
              sanitizedPreview ? `: ${sanitizedPreview}` : ''
            }`
          );
        }

        const specResponse = (await response.json()) as OpenApiSpec;
        const normalizedSpec = apiDocsUtils.normalizeApiSpecForDocs(specResponse, allyApi);
        setApiSpec(normalizedSpec);
      } catch (error) {
        console.error('Error fetching API spec:', error);
        setSpecError(error instanceof Error ? error.message : 'Failed to load API documentation');
      } finally {
        setIsLoadingSpec(false);
      }
    };

    fetchApiSpec();
  }, [token, allyApi]);

  // Create the Stoplight Elements web component
  useEffect(() => {
    const container = containerRef.current;
    if (!apiSpec || !container) return;

    container.innerHTML = '';

    const elementsApi = document.createElement('elements-api') as ElementsApiElement;

    elementsApi.setAttribute('router', 'hash');
    elementsApi.setAttribute('layout', 'sidebar');
    elementsApi.setAttribute('hideTryIt', 'true');

    // Append to DOM first
    container.appendChild(elementsApi);

    // Set the spec as a property AFTER the element is in the DOM
    // This is critical for web components to work correctly
    setTimeout(() => {
      elementsApi.apiDescriptionDocument = apiSpec;
    }, 0);

    // The Voice endpoint is a WebSocket, so Stoplight's auto-generated REST
    // "Request Sample" (e.g. `requests.get(url)`) is misleading and redundant
    // with the examples inside the connection guide, and it crowds the layout.
    // Hide that panel ONLY on the Voice operation (CSS in api-docs.css), keeping
    // request samples on the REST endpoints.
    const VOICE_PATH = '/api/v1/partner/voice';
    const isVoiceOperation = () => {
      // Primary: Stoplight uses a hash router; an operation's path may be
      // pointer-encoded (~1) or percent-encoded, so normalize before matching.
      const hash = decodeURIComponent(window.location.hash).replace(/~1/g, '/');
      if (hash.includes(VOICE_PATH)) return true;
      // Fallback, independent of the hash format: the rendered Voice operation
      // shows its full path in the method bar. (The Overview also mentions it
      // but has no Request Sample panel, so matching there is a harmless no-op.)
      return (container.textContent ?? '').includes(VOICE_PATH);
    };
    const syncVoiceRequestSample = () => {
      container.classList.toggle('hide-voice-request-sample', isVoiceOperation());
    };
    window.addEventListener('hashchange', syncVoiceRequestSample);
    // Stoplight renders operation content asynchronously, so re-evaluate on DOM
    // changes. Observe childList only (not attributes) so our own class toggle
    // does not retrigger the observer.
    const observer = new MutationObserver(syncVoiceRequestSample);
    observer.observe(container, { childList: true, subtree: true });
    syncVoiceRequestSample();

    return () => {
      window.removeEventListener('hashchange', syncVoiceRequestSample);
      observer.disconnect();
      container.innerHTML = '';
    };
  }, [apiSpec]);

  const handleCopyToken = async () => {
    if (token) {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!token || isLoadingSpec) {
    return (
      <div className="py-6">
        <div className="api-keys-header">
          <h1>API Documentation</h1>
          <p>{!token ? 'Loading authentication...' : 'Loading API specification...'}</p>
        </div>

        <div className="au-card flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" label="Loading API documentation..." />
        </div>
      </div>
    );
  }

  if (specError) {
    return (
      <div className="py-6">
        <div className="au-page-header">
          <h1>API Documentation</h1>
          <p className="text-red-600">Failed to load API documentation</p>
        </div>
        <div className="au-card p-6">
          <div className="text-center">
            <p className="text-lg font-semibold text-red-600 mb-2">
              Error Loading API Specification
            </p>
            <p className="text-gray-600 mb-4">{specError}</p>
            <button onClick={() => window.location.reload()} className="api-docs-copy-token-btn">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="api-docs-page">
      <div className="api-docs-header">
        <div className="api-docs-header-content">
          <h1 className="api-keys-title">API Documentation</h1>
          <button
            onClick={handleCopyToken}
            className="api-docs-copy-token-btn"
            title="Copy your auth token to use in Try It requests"
          >
            {copied ? (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"
                  />
                </svg>
                Copy Auth Token
              </>
            )}
          </button>
        </div>
        <p className="api-docs-header-hint">
          Use the copied token in the Authorize dialog to test authenticated endpoints.
        </p>
      </div>
      <div className="api-docs-container" ref={containerRef} />
    </div>
  );
};

export default ApiDocsPage;
